<script setup lang="ts">
import { useEventQuery } from '@/enitites/event/api/queries'
import { useImage } from '@/shared/composables/image'
import { useMyTicketsForEventQuery } from '@/features/tickets/api/queries'
import TicketLeft from '@/features/tickets/assets/TicketLeft.vue'
import AvatarPlaceholder from '@/features/tickets/assets/AvatarPlaceholder.vue'
import { ref, watch } from 'vue'
import { mdiClose } from '@mdi/js'
import { ITicketPurchased } from '@/features/tickets/types'
import SelectedTicket from '@/features/tickets/ui/SelectedTicket.vue'
import TicketAssignmentInterface from '@/features/tickets/ui/TicketAssignmentInterface.vue'
import ChangeTierInterface from '@/features/tickets/ui/ChangeTierInterface.vue'
import RefundTicketInterface from '@/features/tickets/ui/RefundTicketInterface.vue'
import PurchaseReceipt from '@/features/tickets/ui/PurchaseReceipt.vue'

const props = defineProps<{
  eventId: number
  selectedTicketId?: number
}>()

const { getImageById } = useImage()
const { data: event, isLoading: isLoadingEvent, isError: isEventError } = useEventQuery(props.eventId)
const { data: myTickets } = useMyTicketsForEventQuery(props.eventId)

const selectedTicket = ref<ITicketPurchased | undefined>(undefined)
const isShowAssignmentInterface = ref(false)
const isShowSuccessAssignmentMessage = ref(false)
const isShowChangeTierInterface = ref(false)
const isShowRefundInterface = ref(false)
const isShowReceiptInterface = ref(false)

watch(
  () => myTickets.value,
  (newTickets) => {
    if (!newTickets || newTickets.length === 0) return

    if (!selectedTicket.value) {
      const selectedTicketId = props.selectedTicketId || newTickets[0].id
      const foundTicket = newTickets.find((ticket) => ticket.id === selectedTicketId)
      selectedTicket.value = foundTicket || newTickets[0]
    } else {
      // Keep selectedTicket in sync after mutations (e.g. tier change)
      const updated = newTickets.find((ticket) => ticket.id === selectedTicket.value!.id)
      if (updated) selectedTicket.value = updated
    }
  },
  { immediate: true }
)

watch(
  () => selectedTicket.value,
  () => {
    isShowAssignmentInterface.value = false
    isShowChangeTierInterface.value = false
    isShowRefundInterface.value = false
    isShowReceiptInterface.value = false
  }
)
</script>

<template>
  <div class="purchase-interface-container">
    <div class="purchase-ticket-interface">
      <div class="header">
        <template v-if="isLoadingEvent">
          <v-skeleton-loader
            type="image"
            height="84"
            width="84"
          />
          <div class="skeleton-info">
            <v-skeleton-loader type="heading" />
            <v-skeleton-loader
              type="text@2"
              width="150"
            />
          </div>
        </template>

        <template v-else-if="isEventError">
          <p class="error-message">Error loading event data</p>
        </template>
        <template v-else-if="event">
          <v-img
            :src="getImageById(event.image, 168, 168, 'event')"
            height="84"
            width="84"
            class="event-image"
          />
          <div class="event-info">
            <h2 class="event-title">{{ event.title }}</h2>
            <p class="event-info-row">{{ event.dateFlat }}</p>
            <p class="event-info-row">{{ event.timeFlat }}</p>
          </div>
        </template>
      </div>
      <div class="tickets">
        <div
          v-if="myTickets?.length"
          class="tickets-list"
        >
          <button
            v-for="ticket in myTickets"
            :key="ticket.id"
            class="ticket-item"
            :class="{ 'selected-ticket-item': selectedTicket?.id === ticket.id }"
            @click="selectedTicket = ticket"
          >
            <TicketLeft class="ticket-left" />
            <div class="ticket-main">
              <p class="ticket-title">{{ ticket.title }}</p>
              <div class="ticket-assignment">
                <AvatarPlaceholder
                  v-if="!ticket.assignedTo || !('avatar' in ticket.assignedTo) || !ticket.assignedTo.avatar"
                  background-color="#DDDDDD"
                  color="#333333"
                  class="assigned-avatar"
                />
                <v-avatar
                  v-else
                  size="20"
                  class="assigned-avatar"
                >
                  <v-img :src="getImageById(ticket.assignedTo.avatar, 40, 40, 'avatar')" />
                </v-avatar>
                <span class="assigned-to-name">
                  {{
                    !ticket.assignedTo
                      ? 'Unassigned'
                      : 'dancerName' in ticket.assignedTo
                        ? ticket.assignedTo.dancerName
                        : ticket.assignedTo.email || 'Unnamed User'
                  }}
                </span>
              </div>
            </div>
          </button>
        </div>
      </div>
      <div class="selected-ticket">
        <template v-if="selectedTicket">
          <template v-if="isShowAssignmentInterface">
            <TicketAssignmentInterface
              :ticket="selectedTicket"
              @close="isShowAssignmentInterface = false"
              @successful-assignment="isShowSuccessAssignmentMessage = true"
            />
          </template>
          <template v-else-if="isShowChangeTierInterface">
            <ChangeTierInterface
              :current-ticket="selectedTicket"
              :event-id="eventId"
              @close="isShowChangeTierInterface = false"
            />
          </template>
          <template v-else-if="isShowRefundInterface">
            <RefundTicketInterface
              :current-ticket="selectedTicket"
              @close="isShowRefundInterface = false"
              @show-tier-change-interface="isShowChangeTierInterface = true"
            />
          </template>
          <template v-else-if="isShowReceiptInterface">
            <PurchaseReceipt
              :purchase-id="selectedTicket.purchaseId"
              @close="isShowReceiptInterface = false"
            />
          </template>
          <template v-else>
            <SelectedTicket
              :ticket="selectedTicket"
              @show-assignment-interface="isShowAssignmentInterface = true"
              @show-tier-change-interface="isShowChangeTierInterface = true"
              @show-refund-interface="isShowRefundInterface = true"
              @show-receipt-interface="isShowReceiptInterface = true"
            />
          </template>
        </template>
        <v-snackbar
          v-model="isShowSuccessAssignmentMessage"
          absolute
          class="success-assignment-snackbar"
          color="var(--color-green)"
          :elevation="0"
          height="52"
        >
          Email sent to assignee
          <template #action="{ attrs }">
            <v-btn
              v-bind="attrs"
              icon
              @click="isShowSuccessAssignmentMessage = false"
            >
              <v-icon>{{ mdiClose }}</v-icon>
            </v-btn>
          </template>
        </v-snackbar>
      </div>
    </div>
  </div>
</template>

<style scoped lang="scss">
.purchase-interface-container {
  container-type: inline-size;
}

.purchase-ticket-interface {
  display: grid;
  grid-template-areas:
    'header  header'
    'tickets selected';
  grid-template-columns: 270px 1fr;
  grid-template-rows: auto 1fr;
  max-height: 90vh;
  overflow: hidden;

  p {
    margin: 0;
  }

  @container (max-width: 800px) {
    grid-template-areas:
      'header'
      'tickets'
      'selected';
    grid-template-columns: 1fr;
    grid-template-rows: auto auto 1fr;
    overflow-y: auto;
    max-height: unset;
  }

  @container (max-width: 600px) {
    padding-top: 50px;
  }
}

.header {
  position: relative;
  grid-area: header;
  display: flex;
  gap: 16px;
  padding: 40px;
  width: 100%;

  &::after {
    content: '';
    position: absolute;
    bottom: -3px;
    left: 0;
    width: 100%;
    height: 3px;
    background-image: linear-gradient(to bottom, rgb(0 0 0 / 10%), transparent);
  }

  .skeleton-info {
    flex: 1;
    display: flex;
    flex-direction: column;
    justify-content: space-around;
    gap: 8px;
  }

  .event-image {
    flex: 0 0 auto;
    border-radius: 8px;
  }

  .event-info {
    display: flex;
    flex-direction: column;
    justify-content: center;
    gap: 3px;

    .event-title {
      font-weight: var(--font-weight-semi-bold);
      font-size: 24px;
      line-height: 30px;
      color: var(--color-dark-800);
    }

    .event-info-row {
      font-size: 16px;
      line-height: 24px;
      color: var(--color-grey-300);
    }
  }

  @container (max-width: 600px) {
    padding: 24px 12px;
  }
}

.tickets {
  grid-area: tickets;
  display: grid;
  gap: 20px;
  padding: 40px 0;
  overflow-y: auto;

  @container (max-width: 800px) {
    padding: 0;
  }

  .tickets-list {
    display: flex;
    flex-direction: column;
    gap: 8px;
    padding: 2px 20px 5px 40px;
    overflow-y: auto;

    @container (max-width: 800px) {
      flex-direction: row;
      padding: 20px;
    }

    .ticket-item {
      display: flex;
      filter: drop-shadow(0 1px 4px rgb(0 0 0 / 20%));
      text-align: left;

      @container (max-width: 800px) {
        flex: 0 0 auto;
      }

      &.selected-ticket-item {
        filter: drop-shadow(0 2px 0 var(--color-gold-900)) drop-shadow(0 -2px 0 var(--color-gold-900))
          drop-shadow(2px 0 0 var(--color-gold-900)) drop-shadow(-2px 0 0 var(--color-gold-900))
          drop-shadow(0 1px 4px rgb(0 0 0 / 20%));
      }
    }

    .ticket-left {
      flex: 0 0 auto;
      color: var(--color-white);
    }

    .ticket-main {
      display: flex;
      flex-direction: column;
      justify-content: center;
      gap: 8px;
      background-color: var(--color-white);
      border-radius: 0 8px 8px 0;
      padding: 14px 16px 14px 5px;
      margin-left: -1px;
      width: 100%;
      max-width: 188px;
      height: 80px;

      .ticket-title {
        font-size: 16px;
        line-height: 1.2;
        color: var(--color-dark-800);
        white-space: nowrap;
        overflow: hidden;
        text-overflow: ellipsis;
      }

      .ticket-assignment {
        display: flex;
        align-items: center;
        gap: 4px;

        .assigned-avatar {
          flex: 0 0 auto;
        }

        .assigned-to-name {
          flex: 1 1 auto;
          font-size: 12px;
          line-height: 1.2;
          color: var(--color-grey-300);
          white-space: nowrap;
          overflow: hidden;
          text-overflow: ellipsis;
        }
      }
    }
  }

  .tickets-list-actions {
    padding: 0 20px 0 40px;

    @container (max-width: 800px) {
      display: flex;
      justify-content: flex-end;
      padding: 0 20px;
    }

    .tickets-list-actions-button {
      padding: 0;
      text-transform: unset;

      @container (max-width: 800px) {
        max-width: max-content;
      }

      .v-btn__content,
      :deep(.button-text) {
        width: 100%;
      }

      .button-content {
        display: flex;
        align-items: center;
        gap: 8px;
        justify-content: space-between;
        width: 100%;
      }
    }
  }
}

.selected-ticket {
  grid-area: selected;
  position: relative;
  margin: 40px 40px 40px 0;
  padding: 20px;
  border-radius: 16px;
  background-color: var(--color-grey-100);
  border: 1px solid var(--color-grey-600);
  overflow-y: auto;

  @container (max-width: 800px) {
    margin: 20px 20px 40px;
  }
}

.success-assignment-snackbar {
  inset: 26px 18px auto;
  height: auto;
  width: auto;

  :deep(.v-snack__wrapper) {
    margin: 0;
    width: 100%;
  }

  :deep(.v-snack__content) {
    font-weight: 700;
    font-size: 16px;
    line-height: 24px;
  }
}
</style>
